import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

// @mui icons
import {
    AccountBalanceWallet,
    ArrowBack,
    AttachFile,
    CheckCircle,
    CompareArrows,
    Description,
    Email,
    ErrorOutline,
    HourglassTop,
    Notes,
    PictureAsPdf,
    Refresh,
    Save,
    Send,
    WarningAmber,
} from "@mui/icons-material";

// @mui material components
import {
    Alert,
    Button,
    Card,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Finance language
import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";

import {
    COLORS,
    cardSx,
} from "../shared/FinanceUiKit";

const API_URL =
    process.env.REACT_APP_API_URL ||
    "http://localhost:8080/api";

const API_ORIGIN =
    API_URL.replace(
        /\/api\/?$/,
        ""
    );

const resolveFileUrl = (
    value
) => {
    const url =
        String(
            value ||
            ""
        ).trim();

    if (
        !url
    ) {
        return "";
    }

    if (
        /^https?:\/\//i.test(
            url
        )
    ) {
        return url;
    }

    return `${API_ORIGIN}${
        url.startsWith("/")
            ? url
            : `/${url}`
    }`;
};

/*
 * Tüm tek satırlı input ve select alanlarının
 * aynı yükseklikte görünmesi için ortak stil.
 */
const inputSx = {
    "& .MuiOutlinedInput-root": {
        minHeight: "48px",
        height: "48px",
        borderRadius: "8px",
        background: "#FFFFFF",
    },

    "& .MuiInputBase-input": {
        height: "48px",
        boxSizing: "border-box",
        paddingTop: 0,
        paddingBottom: 0,
        display: "flex",
        alignItems: "center",
    },

    "& .MuiSelect-select": {
        minHeight: "48px !important",
        height: "48px !important",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        paddingTop: "0 !important",
        paddingBottom: "0 !important",
    },
};

/*
 * Multiline alanların border / radius / görünümü
 * tek satırlı inputlarla aynı.
 */
const multilineInputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        background: "#FFFFFF",
    },
};

const STATUS_TRANSITIONS = {
    draft: [
        "sent",
        "cancelled",
    ],

    sent: [
        "waiting_response",
        "cancelled",
    ],

    waiting_response: [
        "confirmed",
        "disputed",
        "cancelled",
    ],

    disputed: [
        "waiting_response",
        "confirmed",
        "cancelled",
    ],

    confirmed: [],
    cancelled: [],
};

const toNumber = (value) => {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : 0;
};

const normalizeString = (value) =>
    String(
        value ??
        ""
    ).trim();

const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        normalizeString(value)
    );

const formatMoney = (
    value,
    currency = "EUR",
    language = "en"
) => {
    try {
        return new Intl.NumberFormat(
            language === "tr"
                ? "tr-TR"
                : "en-US",
            {
                style: "currency",

                currency:
                    currency ||
                    "EUR",

                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }
        ).format(
            toNumber(value)
        );
    } catch (error) {
        return `${toNumber(value).toFixed(2)} ${currency || ""}`;
    }
};

const formatDate = (
    value,
    language = "en"
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleDateString(
        language === "tr"
            ? "tr-TR"
            : "en-GB"
    );
};

const formatDateTime = (
    value,
    language = "en"
) => {
    if (
        !value
    ) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleString(
        language === "tr"
            ? "tr-TR"
            : "en-GB",
        {
            dateStyle:
                "medium",

            timeStyle:
                "short",
        }
    );
};

const getCounterpartyResponseTexts = (
    language
) =>
    language ===
    "tr"
        ? {
            title:
                "Karşı Taraf Cevabı",

            subtitle:
                "Müşteri veya tedarikçinin güvenli mutabakat ekranından ilettiği cevap.",

            agreed:
                "Mutabıkız",

            disputed:
                "Mutabık Değiliz",

            respondedBy:
                "Cevaplayan",

            respondedAt:
                "Cevap Tarihi",

            systemBalance:
                "Sistem Bakiyesi",

            declaredBalance:
                "Bildirilen Bakiye",

            balanceType:
                "Bakiye Türü",

            debit:
                "Borç",

            credit:
                "Alacak",

            difference:
                "Fark",

            explanation:
                "Açıklama",

            document:
                "Belge",

            viewDocument:
                "Belgeyi Görüntüle",

            noDocument:
                "Belge eklenmedi",

            waiting:
                "Henüz karşı taraf cevabı bulunmuyor.",

            waitingDescription:
                "Mutabakat e-postası üzerinden cevap verildiğinde bilgiler burada görüntülenecektir.",

            publicResponse:
                "Public mutabakat cevabı",

            taxOffice:
                "Vergi Dairesi",

            phone:
                "Telefon",
        }
        : {
            title:
                "Counterparty Response",

            subtitle:
                "Response submitted by the customer or supplier through the secure reconciliation page.",

            agreed:
                "Agree",

            disputed:
                "Disagree",

            respondedBy:
                "Responded By",

            respondedAt:
                "Response Date",

            systemBalance:
                "System Balance",

            declaredBalance:
                "Declared Balance",

            balanceType:
                "Balance Type",

            debit:
                "Debit",

            credit:
                "Credit",

            difference:
                "Difference",

            explanation:
                "Explanation",

            document:
                "Document",

            viewDocument:
                "View Document",

            noDocument:
                "No document attached",

            waiting:
                "No counterparty response has been received yet.",

            waitingDescription:
                "The response will be displayed here after it is submitted from the reconciliation email.",

            publicResponse:
                "Public reconciliation response",

            taxOffice:
                "Tax Office",

            phone:
                "Phone",
        };

const getStatusConfig = (
    status
) => {
    const normalized =
        String(
            status ||
            "draft"
        ).toLowerCase();

    const config = {
        draft: {
            label: "Draft",

            background: "#F2F4F7",
            textColor: "#344054",

            icon: (
                <Description fontSize="small" />
            ),
        },

        sent: {
            label: "Sent",

            background:
            COLORS.blueLight,

            textColor:
            COLORS.blue,

            icon: (
                <Email fontSize="small" />
            ),
        },

        waiting_response: {
            label: "Waiting Response",

            background:
            COLORS.amberLight,

            textColor:
            COLORS.amber,

            icon: (
                <HourglassTop fontSize="small" />
            ),
        },

        confirmed: {
            label: "Confirmed",

            background:
            COLORS.greenLight,

            textColor:
            COLORS.green,

            icon: (
                <CheckCircle fontSize="small" />
            ),
        },

        disputed: {
            label: "Disputed",

            background:
            COLORS.redLight,

            textColor:
            COLORS.red,

            icon: (
                <WarningAmber fontSize="small" />
            ),
        },

        cancelled: {
            label: "Cancelled",

            background: "#F2F4F7",

            textColor:
            COLORS.subtext,

            icon: (
                <ErrorOutline fontSize="small" />
            ),
        },
    };

    return (
        config[normalized] ||
        config.draft
    );
};

function SummaryCard({
                         title,
                         value,
                         helper,
                         icon,
                         iconBackground,
                         iconColor,
                         valueColor = COLORS.text,
                     }) {
    return (
        <Card
            sx={{
                ...cardSx,
                height: "100%",
            }}
        >
            <MDBox
                p={2.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                height="100%"
                minHeight={130}
            >
                <MDBox>
                    <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        sx={{
                            color:
                            COLORS.subtext,

                            letterSpacing:
                                ".04em",
                        }}
                    >
                        {title}
                    </MDTypography>

                    <MDTypography
                        variant="h5"
                        fontWeight="bold"
                        mt={0.5}
                        sx={{
                            color:
                            valueColor,
                        }}
                    >
                        {value}
                    </MDTypography>

                    {helper ? (
                        <MDTypography
                            variant="caption"
                            mt={0.5}
                            display="block"
                            sx={{
                                color:
                                COLORS.subtext,
                            }}
                        >
                            {helper}
                        </MDTypography>
                    ) : null}
                </MDBox>

                <MDBox
                    width={48}
                    height={48}
                    borderRadius="8px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        background:
                        iconBackground,

                        color:
                        iconColor,

                        flexShrink: 0,
                    }}
                >
                    {icon}
                </MDBox>
            </MDBox>
        </Card>
    );
}

function ReconciliationDetail() {
    const {
        id,
    } = useParams();

    const navigate =
        useNavigate();

    const [
        financeLanguage,
        setFinanceLanguageState,
    ] = useState(
        getFinanceLanguage
    );

    const [
        reconciliation,
        setReconciliation,
    ] = useState(null);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        saving,
        setSaving,
    ] = useState(false);

    const [
        refreshLoading,
        setRefreshLoading,
    ] = useState(false);

    const [
        pdfLoading,
        setPdfLoading,
    ] = useState(false);

    const [
        statusLoading,
        setStatusLoading,
    ] = useState(false);

    const [
        sendLoading,
        setSendLoading,
    ] = useState(false);

    const [
        statusDialogOpen,
        setStatusDialogOpen,
    ] = useState(false);

    const [
        sendDialogOpen,
        setSendDialogOpen,
    ] = useState(false);

    const [
        selectedStatus,
        setSelectedStatus,
    ] = useState("");

    const [
        statusNote,
        setStatusNote,
    ] = useState("");

    const [
        statusError,
        setStatusError,
    ] = useState("");

    const [
        sendError,
        setSendError,
    ] = useState("");

    const [
        error,
        setError,
    ] = useState("");

    const [
        successMessage,
        setSuccessMessage,
    ] = useState("");

    const [
        form,
        setForm,
    ] = useState({
        counterpartyBalance: "",
        notes: "",
        disputeNote: "",
    });

    const [
        emailForm,
        setEmailForm,
    ] = useState({
        to: "",
        cc: "",
        subject: "",
        message: "",
        language:
            getFinanceLanguage(),
    });

    const changeFinanceLanguage = (
        event
    ) => {
        const nextLanguage =
            event.target.value ===
            "tr"
                ? "tr"
                : "en";

        saveFinanceLanguage(
            nextLanguage
        );

        setFinanceLanguageState(
            nextLanguage
        );
    };

    const getAuthHeaders = () => {
        const token =
            localStorage.getItem(
                "token"
            );

        return token
            ? {
                Authorization:
                    `Bearer ${token}`,
            }
            : {};
    };

    const getErrorMessage = (
        requestError,
        fallback
    ) => {
        const serverMessage =
            requestError
                ?.response
                ?.data
                ?.message;

        if (serverMessage) {
            return financeText(
                serverMessage
            );
        }

        return financeText(
            fallback
        );
    };

    const applyReconciliationData = (
        data
    ) => {
        if (!data) {
            return;
        }

        setReconciliation(
            data
        );

        setForm({
            counterpartyBalance:
                data
                    ?.counterpartyBalance !==
                undefined &&
                data
                    ?.counterpartyBalance !==
                null
                    ? data
                        .counterpartyBalance
                    : "",

            notes:
                data
                    ?.notes ||
                "",

            disputeNote:
                data
                    ?.disputeNote ||
                "",
        });
    };

    const fetchReconciliation =
        async () => {
            try {
                setLoading(
                    true
                );

                setError(
                    ""
                );

                const response =
                    await axios.get(
                        `${API_URL}/finance/reconciliations/${id}`,
                        {
                            headers:
                                getAuthHeaders(),
                        }
                    );

                const data =
                    response
                        ?.data
                        ?.data ||
                    response
                        ?.data ||
                    null;

                applyReconciliationData(
                    data
                );
            } catch (
                requestError
                ) {
                console.error(
                    "Reconciliation detail fetch error:",
                    requestError
                );

                setReconciliation(
                    null
                );

                setError(
                    getErrorMessage(
                        requestError,
                        "Reconciliation could not be fetched."
                    )
                );
            } finally {
                setLoading(
                    false
                );
            }
        };

    useEffect(() => {
        fetchReconciliation();
    }, [
        id,
    ]);

    const handleFormChange = (
        field,
        value
    ) => {
        setForm(
            (
                current
            ) => ({
                ...current,

                [field]:
                value,
            })
        );

        setSuccessMessage(
            ""
        );
    };

    // ============================================================
    // SAVE
    // ============================================================

    const handleSave =
        async () => {
            if (
                !reconciliation
            ) {
                return;
            }

            try {
                setSaving(
                    true
                );

                setError(
                    ""
                );

                setSuccessMessage(
                    ""
                );

                const payload = {
                    counterpartyBalance:
                        form
                            .counterpartyBalance ===
                        ""
                            ? null
                            : toNumber(
                                form
                                    .counterpartyBalance
                            ),

                    notes:
                    form.notes,

                    disputeNote:
                    form
                        .disputeNote,
                };

                const response =
                    await axios.patch(
                        `${API_URL}/finance/reconciliations/${id}`,
                        payload,
                        {
                            headers:
                                getAuthHeaders(),
                        }
                    );

                const updated =
                    response
                        ?.data
                        ?.data ||
                    response
                        ?.data;

                applyReconciliationData(
                    updated
                );

                const verifyResponse =
                    await axios.get(
                        `${API_URL}/finance/reconciliations/${id}`,
                        {
                            headers:
                                getAuthHeaders(),
                        }
                    );

                const verifiedData =
                    verifyResponse
                        ?.data
                        ?.data ||
                    verifyResponse
                        ?.data;

                if (
                    verifiedData
                ) {
                    applyReconciliationData(
                        verifiedData
                    );
                }

                setSuccessMessage(
                    financeText(
                        "Reconciliation details saved successfully."
                    )
                );
            } catch (
                requestError
                ) {
                console.error(
                    "Reconciliation update error:",
                    requestError
                );

                setError(
                    getErrorMessage(
                        requestError,
                        "Reconciliation could not be updated."
                    )
                );
            } finally {
                setSaving(
                    false
                );
            }
        };

    // ============================================================
    // REFRESH
    // ============================================================

    const handleRefresh =
        async () => {
            try {
                setRefreshLoading(
                    true
                );

                setError(
                    ""
                );

                setSuccessMessage(
                    ""
                );

                const response =
                    await axios.post(
                        `${API_URL}/finance/reconciliations/${id}/refresh`,
                        {},
                        {
                            headers:
                                getAuthHeaders(),
                        }
                    );

                const updated =
                    response
                        ?.data
                        ?.data ||
                    response
                        ?.data;

                applyReconciliationData(
                    updated
                );

                setSuccessMessage(
                    financeText(
                        "Reconciliation recalculated successfully."
                    )
                );
            } catch (
                requestError
                ) {
                console.error(
                    "Reconciliation refresh error:",
                    requestError
                );

                setError(
                    getErrorMessage(
                        requestError,
                        "Reconciliation could not be recalculated."
                    )
                );
            } finally {
                setRefreshLoading(
                    false
                );
            }
        };

    // ============================================================
    // PDF
    // ============================================================

    const handleOpenPdf =
        async () => {
            if (
                !id ||
                pdfLoading
            ) {
                return;
            }

            const previewWindow =
                window.open(
                    "",
                    "_blank",
                    "width=1200,height=900"
                );

            try {
                setPdfLoading(
                    true
                );

                setError(
                    ""
                );

                setSuccessMessage(
                    ""
                );

                if (
                    previewWindow
                ) {
                    previewWindow.document.open();

                    previewWindow.document.write(`
                        <!DOCTYPE html>
                        <html>
                            <head>
                                <meta charset="UTF-8" />
                                <title>Reconciliation PDF</title>

                                <style>
                                    html,
                                    body {
                                        margin: 0;
                                        padding: 0;
                                        width: 100%;
                                        height: 100%;
                                        background: #f5f7fa;
                                        font-family: Arial, sans-serif;
                                    }

                                    .loading {
                                        width: 100%;
                                        height: 100%;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        color: #667085;
                                        font-size: 14px;
                                    }
                                </style>
                            </head>

                            <body>
                                <div class="loading">
                                    PDF loading...
                                </div>
                            </body>
                        </html>
                    `);

                    previewWindow.document.close();
                }

                await axios.post(
                    `${API_URL}/finance/reconciliations/${id}/pdf`,
                    {
                        language:
                        financeLanguage,

                        force:
                            true,
                    },
                    {
                        headers:
                            getAuthHeaders(),
                    }
                );

                const response =
                    await axios.get(
                        `${API_URL}/finance/reconciliations/${id}/pdf`,
                        {
                            headers:
                                getAuthHeaders(),

                            responseType:
                                "arraybuffer",
                        }
                    );

                const contentType =
                    String(
                        response
                            ?.headers
                            ?.[
                            "content-type"
                            ] ||
                        ""
                    ).toLowerCase();

                if (
                    !contentType.includes(
                        "application/pdf"
                    )
                ) {
                    throw new Error(
                        "Server did not return a PDF file."
                    );
                }

                if (
                    !response.data ||
                    response
                        .data
                        .byteLength ===
                    0
                ) {
                    throw new Error(
                        "Generated PDF file is empty."
                    );
                }

                const firstBytes =
                    new Uint8Array(
                        response.data.slice(
                            0,
                            5
                        )
                    );

                const signature =
                    String.fromCharCode(
                        ...firstBytes
                    );

                if (
                    signature !==
                    "%PDF-"
                ) {
                    throw new Error(
                        "Generated file is not a valid PDF."
                    );
                }

                const pdfBlob =
                    new Blob(
                        [
                            response.data,
                        ],
                        {
                            type:
                                "application/pdf",
                        }
                    );

                const blobUrl =
                    URL.createObjectURL(
                        pdfBlob
                    );

                if (
                    previewWindow &&
                    !previewWindow.closed
                ) {
                    previewWindow.document.open();

                    previewWindow.document.write(`
                        <!DOCTYPE html>
                        <html>
                            <head>
                                <meta charset="UTF-8" />
                                <title>Reconciliation PDF</title>

                                <style>
                                    html,
                                    body {
                                        margin: 0;
                                        padding: 0;
                                        width: 100%;
                                        height: 100%;
                                        overflow: hidden;
                                        background: #525659;
                                    }

                                    embed {
                                        width: 100%;
                                        height: 100%;
                                        border: none;
                                    }
                                </style>
                            </head>

                            <body>
                                <embed
                                    src="${blobUrl}"
                                    type="application/pdf"
                                />
                            </body>
                        </html>
                    `);

                    previewWindow.document.close();
                } else {
                    const link =
                        document.createElement(
                            "a"
                        );

                    link.href =
                        blobUrl;

                    link.target =
                        "_blank";

                    link.rel =
                        "noopener noreferrer";

                    document.body.appendChild(
                        link
                    );

                    link.click();

                    document.body.removeChild(
                        link
                    );
                }

                window.setTimeout(
                    () => {
                        URL.revokeObjectURL(
                            blobUrl
                        );
                    },
                    10 *
                    60 *
                    1000
                );

                setSuccessMessage(
                    financeText(
                        "Reconciliation PDF generated successfully."
                    )
                );
            } catch (
                requestError
                ) {
                console.error(
                    "Reconciliation PDF error:",
                    requestError
                );

                if (
                    previewWindow &&
                    !previewWindow.closed
                ) {
                    previewWindow.close();
                }

                let message =
                    requestError
                        ?.message ||
                    "Reconciliation PDF could not be opened.";

                if (
                    requestError
                        ?.response
                ) {
                    try {
                        const responseData =
                            requestError
                                .response
                                .data;

                        if (
                            responseData instanceof
                            ArrayBuffer
                        ) {
                            const decoder =
                                new TextDecoder(
                                    "utf-8"
                                );

                            const text =
                                decoder.decode(
                                    responseData
                                );

                            const parsed =
                                JSON.parse(
                                    text
                                );

                            message =
                                parsed
                                    ?.message ||
                                message;
                        } else if (
                            responseData
                                ?.message
                        ) {
                            message =
                                responseData
                                    .message;
                        }
                    } catch (
                        parseError
                        ) {
                        console.warn(
                            "PDF error response parse error:",
                            parseError
                        );
                    }
                }

                setError(
                    financeText(
                        message
                    )
                );
            } finally {
                setPdfLoading(
                    false
                );
            }
        };

    // ============================================================
    // EMAIL
    // ============================================================

    const getDefaultEmailSubject = (
        language
    ) => {
        const reconciliationNo =
            reconciliation
                ?.reconciliationNo ||
            "";

        const partyName =
            reconciliation
                ?.snapshot
                ?.party
                ?.name ||
            "";

        if (
            language ===
            "tr"
        ) {
            return [
                "Cari Hesap Mutabakatı",
                reconciliationNo,
                partyName,
            ]
                .filter(Boolean)
                .join(" - ");
        }

        return [
            "Account Reconciliation",
            reconciliationNo,
            partyName,
        ]
            .filter(Boolean)
            .join(" - ");
    };

    const getDefaultEmailMessage = (
        language
    ) => {
        const partyName =
            reconciliation
                ?.snapshot
                ?.party
                ?.name ||
            (
                language ===
                "tr"
                    ? "Yetkili"
                    : "Sir/Madam"
            );

        const balanceDate =
            formatDate(
                reconciliation
                    ?.periodEnd,
                language
            );

        if (
            language ===
            "tr"
        ) {
            return `Sayın ${partyName},

${balanceDate} tarihi itibarıyla cari hesap mutabakatımız ekte bilginize sunulmuştur.

Kayıtlarınızı kontrol ederek tarafımıza dönüş sağlamanızı rica ederiz.

İyi çalışmalar dileriz.`;
        }

        return `Dear ${partyName},

Please find attached our account reconciliation as of ${balanceDate}.

Kindly review your records and revert to us with your confirmation.

Best regards.`;
    };

    const openSendDialog =
        () => {
            const language =
                financeLanguage ===
                "tr"
                    ? "tr"
                    : "en";

            setSendError(
                ""
            );

            setEmailForm({
                to:
                    reconciliation
                        ?.snapshot
                        ?.party
                        ?.email ||
                    "",

                cc: "",

                subject:
                    getDefaultEmailSubject(
                        language
                    ),

                message:
                    getDefaultEmailMessage(
                        language
                    ),

                language,
            });

            setSendDialogOpen(
                true
            );
        };

    const closeSendDialog =
        () => {
            if (
                sendLoading
            ) {
                return;
            }

            setSendDialogOpen(
                false
            );

            setSendError(
                ""
            );
        };

    const handleEmailFormChange = (
        field,
        value
    ) => {
        setSendError(
            ""
        );

        if (
            field ===
            "language"
        ) {
            const language =
                value ===
                "tr"
                    ? "tr"
                    : "en";

            setEmailForm(
                (
                    current
                ) => ({
                    ...current,

                    language,

                    subject:
                        getDefaultEmailSubject(
                            language
                        ),

                    message:
                        getDefaultEmailMessage(
                            language
                        ),
                })
            );

            return;
        }

        setEmailForm(
            (
                current
            ) => ({
                ...current,

                [field]:
                value,
            })
        );
    };

    const handleSendEmail =
        async () => {
            const recipient =
                normalizeString(
                    emailForm.to
                );

            if (
                !recipient
            ) {
                setSendError(
                    financeText(
                        "Recipient email is required."
                    )
                );

                return;
            }

            if (
                !isValidEmail(
                    recipient
                )
            ) {
                setSendError(
                    financeText(
                        "Recipient email address is invalid."
                    )
                );

                return;
            }

            if (
                !normalizeString(
                    emailForm.subject
                )
            ) {
                setSendError(
                    financeText(
                        "Subject is required."
                    )
                );

                return;
            }

            if (
                !normalizeString(
                    emailForm.message
                )
            ) {
                setSendError(
                    financeText(
                        "Message is required."
                    )
                );

                return;
            }

            try {
                setSendLoading(
                    true
                );

                setSendError(
                    ""
                );

                setError(
                    ""
                );

                setSuccessMessage(
                    ""
                );

                const cc =
                    normalizeString(
                        emailForm.cc
                    )
                        ? emailForm.cc
                            .split(
                                /[,;]/
                            )
                            .map(
                                (
                                    item
                                ) =>
                                    normalizeString(
                                        item
                                    )
                            )
                            .filter(Boolean)
                        : [];

                await axios.post(
                    `${API_URL}/finance/reconciliations/${id}/send-email`,
                    {
                        to:
                        recipient,

                        cc,

                        subject:
                            normalizeString(
                                emailForm.subject
                            ),

                        message:
                            normalizeString(
                                emailForm.message
                            ),

                        language:
                            emailForm.language ===
                            "tr"
                                ? "tr"
                                : "en",
                    },
                    {
                        headers:
                            getAuthHeaders(),
                    }
                );

                if (
                    String(
                        reconciliation
                            ?.status ||
                        ""
                    ).toLowerCase() ===
                    "draft"
                ) {
                    try {
                        await axios.patch(
                            `${API_URL}/finance/reconciliations/${id}/status`,
                            {
                                status:
                                    "sent",

                                note:
                                    financeText(
                                        "Reconciliation sent by email."
                                    ),
                            },
                            {
                                headers:
                                    getAuthHeaders(),
                            }
                        );
                    } catch (
                        statusRequestError
                        ) {
                        console.error(
                            "Email sent but reconciliation status could not be updated:",
                            statusRequestError
                        );
                    }
                }

                const verifyResponse =
                    await axios.get(
                        `${API_URL}/finance/reconciliations/${id}`,
                        {
                            headers:
                                getAuthHeaders(),
                        }
                    );

                const updated =
                    verifyResponse
                        ?.data
                        ?.data ||
                    verifyResponse
                        ?.data;

                if (
                    updated
                ) {
                    applyReconciliationData(
                        updated
                    );
                }

                setSendDialogOpen(
                    false
                );

                setSendError(
                    ""
                );

                setSuccessMessage(
                    financeText(
                        "Reconciliation email sent successfully."
                    )
                );
            } catch (
                requestError
                ) {
                console.error(
                    "Reconciliation email send error:",
                    requestError
                );

                setSendError(
                    getErrorMessage(
                        requestError,
                        "Reconciliation email could not be sent."
                    )
                );
            } finally {
                setSendLoading(
                    false
                );
            }
        };

    // ============================================================
    // STATUS
    // ============================================================

    const openStatusDialog = (
        status
    ) => {
        setSelectedStatus(
            status
        );

        setStatusError(
            ""
        );

        if (
            status ===
            "disputed"
        ) {
            setStatusNote(
                form
                    .disputeNote ||
                ""
            );
        } else {
            setStatusNote(
                ""
            );
        }

        setStatusDialogOpen(
            true
        );
    };

    const closeStatusDialog =
        () => {
            if (
                statusLoading
            ) {
                return;
            }

            setStatusDialogOpen(
                false
            );

            setSelectedStatus(
                ""
            );

            setStatusNote(
                ""
            );

            setStatusError(
                ""
            );
        };

    // ============================================================
    // VALUES
    // ============================================================

    const currency =
        reconciliation
            ?.currency ||
        "EUR";

    const currentBalance =
        reconciliation
            ?.snapshot
            ?.closingBalance ??
        reconciliation
            ?.closingBalance ??
        0;

    const counterpartyResponseTexts =
        getCounterpartyResponseTexts(
            financeLanguage
        );

    const counterpartyResponse =
        String(
            reconciliation
                ?.counterpartyResponse ||
            "pending"
        )
            .trim()
            .toLowerCase();

    const hasPublicResponse =
        counterpartyResponse ===
        "agreed" ||
        counterpartyResponse ===
        "disputed";

    const isPublicAgreed =
        counterpartyResponse ===
        "agreed";

    const isPublicDisputed =
        counterpartyResponse ===
        "disputed";

    const responseBalanceType =
        String(
            reconciliation
                ?.counterpartyResponseBalanceType ||
            ""
        )
            .trim()
            .toLowerCase();

    const rawPublicBalance =
        reconciliation
            ?.counterpartyResponseBalance;

    const publicCounterpartyBalance =
        isPublicAgreed
            ? toNumber(
                currentBalance
            )
            : (
                isPublicDisputed &&
                rawPublicBalance !==
                null &&
                rawPublicBalance !==
                undefined
                    ? (
                        responseBalanceType ===
                        "credit"
                            ? -Math.abs(
                                toNumber(
                                    rawPublicBalance
                                )
                            )
                            : Math.abs(
                                toNumber(
                                    rawPublicBalance
                                )
                            )
                    )
                    : null
            );

    const manualCounterpartyBalance =
        form
            .counterpartyBalance ===
        ""
            ? null
            : toNumber(
                form
                    .counterpartyBalance
            );

    /*
     * Public cevap varsa müşterinin/tedarikçinin
     * bildirdiği bakiye önceliklidir.
     *
     * Public cevap yoksa eski manuel Finance alanı
     * fallback olarak çalışmaya devam eder.
     */
    const counterpartyBalance =
        hasPublicResponse
            ? publicCounterpartyBalance
            : manualCounterpartyBalance;

    const differenceAmount =
        counterpartyBalance ===
        null
            ? 0
            : toNumber(
                currentBalance
            ) -
            toNumber(
                counterpartyBalance
            );

    const isBalanced =
        counterpartyBalance !==
        null &&
        Math.abs(
            differenceAmount
        ) <
        0.01;

    const responseDocumentUrl =
        resolveFileUrl(
            reconciliation
                ?.counterpartyResponseDocumentUrl
        );

    const responseDocumentName =
        reconciliation
            ?.counterpartyResponseDocumentName ||
        "";

    const responseNote =
        reconciliation
            ?.counterpartyResponseNote ||
        reconciliation
            ?.disputeNote ||
        "";

    const responseBalanceTypeLabel =
        responseBalanceType ===
        "credit"
            ? counterpartyResponseTexts.credit
            : responseBalanceType ===
            "debit"
                ? counterpartyResponseTexts.debit
                : "-";

    const currentStatus =
        String(
            reconciliation
                ?.status ||
            "draft"
        ).toLowerCase();

    const statusConfig =
        getStatusConfig(
            currentStatus
        );

    const availableStatuses =
        STATUS_TRANSITIONS[
            currentStatus
            ] || [];

    const handleStatusUpdate =
        async () => {
            const nextStatus =
                String(
                    selectedStatus ||
                    ""
                )
                    .trim()
                    .toLowerCase();

            if (
                !nextStatus
            ) {
                setStatusError(
                    financeText(
                        "Please select a status."
                    )
                );

                return;
            }

            if (
                nextStatus ===
                "disputed" &&
                !String(
                    statusNote ||
                    form.disputeNote ||
                    ""
                ).trim()
            ) {
                setStatusError(
                    financeText(
                        "A dispute note is required."
                    )
                );

                return;
            }

            if (
                nextStatus ===
                "confirmed" &&
                !isBalanced
            ) {
                setStatusError(
                    financeText(
                        "Reconciliation with a balance difference cannot be confirmed."
                    )
                );

                return;
            }

            try {
                setStatusLoading(
                    true
                );

                setStatusError(
                    ""
                );

                setError(
                    ""
                );

                setSuccessMessage(
                    ""
                );

                if (
                    nextStatus ===
                    "confirmed" ||
                    nextStatus ===
                    "disputed"
                ) {
                    await axios.patch(
                        `${API_URL}/finance/reconciliations/${id}`,
                        {
                            counterpartyBalance:
                                form
                                    .counterpartyBalance ===
                                ""
                                    ? null
                                    : toNumber(
                                        form
                                            .counterpartyBalance
                                    ),

                            notes:
                            form.notes,

                            disputeNote:
                                nextStatus ===
                                "disputed"
                                    ? (
                                        statusNote ||
                                        form
                                            .disputeNote
                                    )
                                    : form
                                        .disputeNote,
                        },
                        {
                            headers:
                                getAuthHeaders(),
                        }
                    );
                }

                const response =
                    await axios.patch(
                        `${API_URL}/finance/reconciliations/${id}/status`,
                        {
                            status:
                            nextStatus,

                            note:
                            statusNote,
                        },
                        {
                            headers:
                                getAuthHeaders(),
                        }
                    );

                const responseData =
                    response
                        ?.data
                        ?.data ||
                    response
                        ?.data;

                if (
                    !responseData
                ) {
                    throw new Error(
                        "Status update response is empty."
                    );
                }

                applyReconciliationData(
                    responseData
                );

                const verifyResponse =
                    await axios.get(
                        `${API_URL}/finance/reconciliations/${id}`,
                        {
                            headers:
                                getAuthHeaders(),
                        }
                    );

                const verifiedData =
                    verifyResponse
                        ?.data
                        ?.data ||
                    verifyResponse
                        ?.data;

                if (
                    !verifiedData
                ) {
                    throw new Error(
                        "Updated reconciliation could not be verified."
                    );
                }

                const verifiedStatus =
                    String(
                        verifiedData
                            ?.status ||
                        ""
                    )
                        .trim()
                        .toLowerCase();

                if (
                    verifiedStatus !==
                    nextStatus
                ) {
                    throw new Error(
                        `Status was not persisted. Expected ${nextStatus}, received ${verifiedStatus || "empty"}.`
                    );
                }

                applyReconciliationData(
                    verifiedData
                );

                setStatusDialogOpen(
                    false
                );

                setSelectedStatus(
                    ""
                );

                setStatusNote(
                    ""
                );

                setStatusError(
                    ""
                );

                setSuccessMessage(
                    financeText(
                        "Reconciliation status updated successfully."
                    )
                );
            } catch (
                requestError
                ) {
                console.error(
                    "Reconciliation status update error:",
                    requestError
                );

                const serverMessage =
                    requestError
                        ?.response
                        ?.data
                        ?.message;

                setStatusError(
                    financeText(
                        serverMessage ||
                        requestError
                            ?.message ||
                        "Reconciliation status could not be updated."
                    )
                );
            } finally {
                setStatusLoading(
                    false
                );
            }
        };

    // ============================================================
    // LOADING
    // ============================================================

    if (
        loading
    ) {
        return (
            <DashboardLayout>
                <DashboardNavbar />

                <MDBox
                    minHeight="75vh"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                >
                    <CircularProgress />

                    <MDTypography
                        variant="button"
                        sx={{
                            color:
                            COLORS.subtext,
                        }}
                    >
                        {financeText(
                            "Loading reconciliation..."
                        )}
                    </MDTypography>
                </MDBox>

                <Footer />
            </DashboardLayout>
        );
    }

    if (
        !reconciliation
    ) {
        return (
            <DashboardLayout>
                <DashboardNavbar />

                <MDBox
                    pt={6}
                    pb={3}
                >
                    <Alert
                        severity="error"
                        sx={{
                            borderRadius:
                                "8px",
                        }}
                    >
                        {error ||
                            financeText(
                                "Reconciliation could not be found."
                            )}
                    </Alert>

                    <MDButton
                        variant="text"
                        color="info"
                        onClick={() =>
                            navigate(
                                "/invoice/finance/reconciliations"
                            )
                        }
                        sx={{
                            mt: 2,
                        }}
                    >
                        <ArrowBack />

                        &nbsp;

                        {financeText(
                            "Back to Reconciliations"
                        )}
                    </MDButton>
                </MDBox>

                <Footer />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight:
                        "100vh",

                    background:
                    COLORS.page,

                    mx: {
                        xs: -2,
                        md: -3,
                    },

                    px: {
                        xs: 2,
                        md: 3,
                    },
                }}
            >
                {/* ================================================= */}
                {/* HERO */}
                {/* ================================================= */}

                <MDBox
                    mb={2.5}
                    p={{
                        xs: 2.5,
                        md: 3.5,
                    }}
                    borderRadius="20px"
                    sx={{
                        color:
                        COLORS.white,

                        background:
                            "linear-gradient(135deg, #0B1F3A 0%, #175CD3 100%)",

                        boxShadow:
                            "0 16px 40px rgba(11,31,58,.16)",
                    }}
                >
                    <MDBox
                        mb={2}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={2}
                        flexWrap="wrap"
                    >
                        <MDButton
                            variant="text"
                            onClick={() =>
                                navigate(
                                    "/invoice/finance/reconciliations"
                                )
                            }
                            sx={{
                                px: 0,

                                color:
                                    "rgba(255,255,255,.78) !important",
                            }}
                        >
                            <ArrowBack fontSize="small" />

                            &nbsp;

                            {financeText(
                                "Back to Reconciliations"
                            )}
                        </MDButton>

                        <MDBox
                            sx={{
                                display:
                                    "flex",

                                alignItems:
                                    "center",

                                gap: "4px",

                                p: "4px",

                                background:
                                    "rgba(255,255,255,.10)",

                                border:
                                    "1px solid rgba(255,255,255,.20)",

                                borderRadius:
                                    "12px",
                            }}
                        >
                            {[
                                {
                                    value: "en",
                                    label: "EN",
                                    flag: "/flags/gb.svg",
                                    title: "English",
                                },

                                {
                                    value: "tr",
                                    label: "TR",
                                    flag: "/flags/tr.svg",
                                    title: "Türkçe",
                                },
                            ].map(
                                (
                                    language
                                ) => (
                                    <MDBox
                                        key={
                                            language.value
                                        }
                                        component="button"
                                        type="button"
                                        onClick={() =>
                                            changeFinanceLanguage({
                                                target: {
                                                    value:
                                                    language.value,
                                                },
                                            })
                                        }
                                        sx={{
                                            appearance:
                                                "none",

                                            border:
                                                "none",

                                            cursor:
                                                "pointer",

                                            display:
                                                "flex",

                                            alignItems:
                                                "center",

                                            gap: "7px",

                                            height: 36,
                                            minWidth: 72,

                                            px: 1,

                                            borderRadius:
                                                "8px",

                                            background:
                                                financeLanguage ===
                                                language.value
                                                    ? "#FFFFFF"
                                                    : "transparent",

                                            color:
                                                financeLanguage ===
                                                language.value
                                                    ? COLORS.navy
                                                    : "#FFFFFF",
                                        }}
                                    >
                                        <MDBox
                                            component="img"
                                            src={
                                                language.flag
                                            }
                                            alt={
                                                language.title
                                            }
                                            sx={{
                                                width: 24,
                                                height: 17,

                                                objectFit:
                                                    "cover",

                                                borderRadius:
                                                    "3px",
                                            }}
                                        />

                                        <MDTypography
                                            component="span"
                                            sx={{
                                                fontSize:
                                                    "12px",

                                                fontWeight:
                                                    800,

                                                color:
                                                    "inherit",
                                            }}
                                        >
                                            {
                                                language.label
                                            }
                                        </MDTypography>
                                    </MDBox>
                                )
                            )}
                        </MDBox>
                    </MDBox>

                    <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{
                            xs:
                                "flex-start",

                            md:
                                "center",
                        }}
                        flexDirection={{
                            xs:
                                "column",

                            md:
                                "row",
                        }}
                        gap={2}
                    >
                        <MDBox>
                            <MDBox
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mb={1}
                            >
                                <CompareArrows />

                                <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            "rgba(255,255,255,.72)",

                                        letterSpacing:
                                            ".1em",
                                    }}
                                >
                                    {financeText(
                                        "RECONCILIATION DETAIL"
                                    )}
                                </MDTypography>
                            </MDBox>

                            <MDBox
                                display="flex"
                                alignItems="center"
                                gap={1.5}
                                flexWrap="wrap"
                            >
                                <MDTypography
                                    variant="h3"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                        COLORS.white,
                                    }}
                                >
                                    {reconciliation
                                            .reconciliationNo ||
                                        financeText(
                                            "Reconciliation"
                                        )}
                                </MDTypography>

                                <Chip
                                    icon={
                                        statusConfig.icon
                                    }
                                    label={
                                        financeText(
                                            statusConfig.label
                                        )
                                    }
                                    size="small"
                                    sx={{
                                        background:
                                        statusConfig.background,

                                        color:
                                        statusConfig.textColor,

                                        fontWeight:
                                            700,
                                    }}
                                />
                            </MDBox>

                            <MDTypography
                                variant="body2"
                                mt={1}
                                sx={{
                                    color:
                                        "rgba(255,255,255,.74)",
                                }}
                            >
                                {reconciliation
                                        ?.snapshot
                                        ?.party
                                        ?.name ||
                                    "-"}

                                {" • "}

                                {financeText(
                                    "Balance Date"
                                )}

                                {": "}

                                {formatDate(
                                    reconciliation
                                        .periodEnd,
                                    financeLanguage
                                )}

                                {" • "}

                                {currency}
                            </MDTypography>
                        </MDBox>

                        <MDBox
                            display="flex"
                            gap={1}
                            flexWrap="wrap"
                        >
                            {currentStatus ===
                            "draft" ? (
                                <MDButton
                                    variant="contained"
                                    onClick={
                                        handleRefresh
                                    }
                                    disabled={
                                        refreshLoading ||
                                        saving ||
                                        pdfLoading ||
                                        statusLoading ||
                                        sendLoading
                                    }
                                    sx={{
                                        color:
                                            "#FFFFFF !important",
                                    }}
                                >
                                    {refreshLoading ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    ) : (
                                        <>
                                            <Refresh
                                                sx={{
                                                    mr: 0.6,
                                                }}
                                            />

                                            {financeText(
                                                "Recalculate"
                                            )}
                                        </>
                                    )}
                                </MDButton>
                            ) : null}

                            <MDButton
                                variant="contained"
                                onClick={
                                    handleOpenPdf
                                }
                                disabled={
                                    pdfLoading ||
                                    saving ||
                                    statusLoading ||
                                    sendLoading
                                }
                            >
                                <PictureAsPdf
                                    sx={{
                                        mr: 0.6,
                                    }}
                                />

                                {financeText(
                                    "PDF"
                                )}
                            </MDButton>

                            <MDButton
                                variant="contained"
                                onClick={
                                    openSendDialog
                                }
                                disabled={
                                    currentStatus ===
                                    "cancelled" ||
                                    sendLoading ||
                                    saving ||
                                    pdfLoading ||
                                    statusLoading
                                }
                                sx={{
                                    background:
                                        "#FFFFFF !important",

                                    color:
                                        `${COLORS.navy} !important`,
                                }}
                            >
                                <Email
                                    sx={{
                                        mr: 0.6,
                                    }}
                                />

                                {financeText(
                                    "Send"
                                )}
                            </MDButton>
                        </MDBox>
                    </MDBox>
                </MDBox>

                {/* ================================================= */}
                {/* ALERTS */}
                {/* ================================================= */}

                {error ? (
                    <Alert
                        severity="error"
                        sx={{
                            mb: 2.5,

                            borderRadius:
                                "8px",
                        }}
                    >
                        {error}
                    </Alert>
                ) : null}

                {successMessage ? (
                    <Alert
                        severity="success"
                        sx={{
                            mb: 2.5,

                            borderRadius:
                                "8px",
                        }}
                    >
                        {successMessage}
                    </Alert>
                ) : null}

                {/* ================================================= */}
                {/* BALANCE SUMMARY */}
                {/* ================================================= */}

                <Grid
                    container
                    spacing={2}
                    mb={2.5}
                >
                    <Grid
                        item
                        xs={12}
                        md={4}
                    >
                        <SummaryCard
                            title={
                                financeText(
                                    "CURRENT BALANCE"
                                )
                            }
                            value={
                                formatMoney(
                                    currentBalance,
                                    currency,
                                    financeLanguage
                                )
                            }
                            helper={`${financeText(
                                "Balance as of"
                            )} ${formatDate(
                                reconciliation
                                    .periodEnd,
                                financeLanguage
                            )}`}
                            icon={
                                <AccountBalanceWallet />
                            }
                            iconBackground={
                                COLORS.purpleLight
                            }
                            iconColor={
                                COLORS.purple
                            }
                            valueColor={
                                COLORS.purple
                            }
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={4}
                    >
                        <SummaryCard
                            title={
                                financeText(
                                    "COUNTERPARTY BALANCE"
                                )
                            }
                            value={
                                counterpartyBalance ===
                                null
                                    ? "-"
                                    : formatMoney(
                                        counterpartyBalance,
                                        currency,
                                        financeLanguage
                                    )
                            }
                            helper={
                                counterpartyBalance ===
                                null
                                    ? financeText(
                                        "Counterparty balance not entered"
                                    )
                                    : financeText(
                                        "Balance declared by counterparty"
                                    )
                            }
                            icon={
                                <CompareArrows />
                            }
                            iconBackground={
                                COLORS.blueLight
                            }
                            iconColor={
                                COLORS.blue
                            }
                            valueColor={
                                COLORS.blue
                            }
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={4}
                    >
                        <SummaryCard
                            title={
                                financeText(
                                    "DIFFERENCE"
                                )
                            }
                            value={
                                counterpartyBalance ===
                                null
                                    ? "-"
                                    : formatMoney(
                                        differenceAmount,
                                        currency,
                                        financeLanguage
                                    )
                            }
                            helper={
                                counterpartyBalance ===
                                null
                                    ? financeText(
                                        "Counterparty balance not entered"
                                    )
                                    : isBalanced
                                        ? financeText(
                                            "Balances match"
                                        )
                                        : financeText(
                                            "Requires review"
                                        )
                            }
                            icon={
                                isBalanced ? (
                                    <CheckCircle />
                                ) : (
                                    <WarningAmber />
                                )
                            }
                            iconBackground={
                                counterpartyBalance ===
                                null
                                    ? "#F2F4F7"
                                    : isBalanced
                                        ? COLORS.greenLight
                                        : COLORS.redLight
                            }
                            iconColor={
                                counterpartyBalance ===
                                null
                                    ? COLORS.subtext
                                    : isBalanced
                                        ? COLORS.green
                                        : COLORS.red
                            }
                            valueColor={
                                counterpartyBalance ===
                                null
                                    ? COLORS.text
                                    : isBalanced
                                        ? COLORS.green
                                        : COLORS.red
                            }
                        />
                    </Grid>
                </Grid>

                {/* ================================================= */}
                {/* MAIN CONTENT */}
                {/* ================================================= */}

                <Grid
                    container
                    spacing={2.5}
                >
                    <Grid
                        item
                        xs={12}
                        xl={8}
                    >
                        {/* CURRENT BALANCE DETAIL */}

                        <Card
                            sx={{
                                ...cardSx,

                                mb: 2.5,
                            }}
                        >
                            <MDBox
                                px={3}
                                py={2.7}
                            >
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={2}
                                    flexWrap="wrap"
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                        >
                                            {financeText(
                                                "Balance Summary"
                                            )}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            mt={0.4}
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText(
                                                "Current balance calculated from all records up to the selected balance date."
                                            )}
                                        </MDTypography>
                                    </MDBox>

                                    <Chip
                                        label={
                                            formatDate(
                                                reconciliation
                                                    .periodEnd,
                                                financeLanguage
                                            )
                                        }
                                        sx={{
                                            background:
                                            COLORS.blueLight,

                                            color:
                                            COLORS.blue,

                                            fontWeight:
                                                700,
                                        }}
                                    />
                                </MDBox>
                            </MDBox>

                            <Divider />

                            <MDBox
                                p={{
                                    xs: 2.5,
                                    md: 4,
                                }}
                            >
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: {
                                            xs: 2.5,
                                            md: 3.5,
                                        },

                                        borderRadius:
                                            "10px",

                                        borderColor:
                                        COLORS.border,

                                        background:
                                            "#F8FAFC",
                                    }}
                                >
                                    <Grid
                                        container
                                        spacing={3}
                                        alignItems="center"
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            md={7}
                                        >
                                            <MDTypography
                                                variant="caption"
                                                fontWeight="bold"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,

                                                    letterSpacing:
                                                        ".05em",
                                                }}
                                            >
                                                {financeText(
                                                    "CURRENT BALANCE"
                                                )}
                                            </MDTypography>

                                            <MDTypography
                                                variant="h2"
                                                fontWeight="bold"
                                                mt={0.7}
                                                sx={{
                                                    color:
                                                    COLORS.navy,
                                                }}
                                            >
                                                {formatMoney(
                                                    currentBalance,
                                                    currency,
                                                    financeLanguage
                                                )}
                                            </MDTypography>

                                            <MDTypography
                                                variant="body2"
                                                mt={1}
                                                sx={{
                                                    color:
                                                    COLORS.subtext,
                                                }}
                                            >
                                                {financeText(
                                                    "Balance as of"
                                                )}

                                                {" "}

                                                {formatDate(
                                                    reconciliation
                                                        .periodEnd,
                                                    financeLanguage
                                                )}
                                            </MDTypography>
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            md={5}
                                        >
                                            <MDBox
                                                display="flex"
                                                justifyContent={{
                                                    xs:
                                                        "flex-start",

                                                    md:
                                                        "flex-end",
                                                }}
                                            >
                                                <MDBox
                                                    width={82}
                                                    height={82}
                                                    borderRadius="10px"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    sx={{
                                                        background:
                                                        COLORS.purpleLight,

                                                        color:
                                                        COLORS.purple,
                                                    }}
                                                >
                                                    <AccountBalanceWallet
                                                        sx={{
                                                            fontSize:
                                                                42,
                                                        }}
                                                    />
                                                </MDBox>
                                            </MDBox>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </MDBox>
                        </Card>

                        {/* ================================================= */}
                        {/* COUNTERPARTY PUBLIC RESPONSE */}
                        {/* ================================================= */}

                        <Card
                            sx={{
                                ...cardSx,

                                mb: 2.5,
                            }}
                        >
                            <MDBox
                                px={3}
                                py={2.7}
                            >
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={2}
                                    flexWrap="wrap"
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                        >
                                            {
                                                counterpartyResponseTexts
                                                    .title
                                            }
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            mt={0.4}
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {
                                                counterpartyResponseTexts
                                                    .subtitle
                                            }
                                        </MDTypography>
                                    </MDBox>

                                    {hasPublicResponse ? (
                                        <Chip
                                            icon={
                                                isPublicAgreed ? (
                                                    <CheckCircle
                                                        fontSize="small"
                                                    />
                                                ) : (
                                                    <WarningAmber
                                                        fontSize="small"
                                                    />
                                                )
                                            }
                                            label={
                                                isPublicAgreed
                                                    ? counterpartyResponseTexts
                                                        .agreed
                                                    : counterpartyResponseTexts
                                                        .disputed
                                            }
                                            size="small"
                                            sx={{
                                                background:
                                                    isPublicAgreed
                                                        ? COLORS.greenLight
                                                        : COLORS.redLight,

                                                color:
                                                    isPublicAgreed
                                                        ? COLORS.green
                                                        : COLORS.red,

                                                fontWeight:
                                                    800,

                                                "& .MuiChip-icon":
                                                    {
                                                        color:
                                                            "inherit",
                                                    },
                                            }}
                                        />
                                    ) : (
                                        <Chip
                                            label={
                                                currentStatus ===
                                                "sent"
                                                    ? financeText(
                                                        "Waiting Response"
                                                    )
                                                    : financeText(
                                                        "Draft"
                                                    )
                                            }
                                            size="small"
                                            sx={{
                                                background:
                                                COLORS.amberLight,

                                                color:
                                                COLORS.amber,

                                                fontWeight:
                                                    700,
                                            }}
                                        />
                                    )}
                                </MDBox>
                            </MDBox>

                            <Divider />

                            {hasPublicResponse ? (
                                <MDBox
                                    p={3}
                                >
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            mb: 2.5,

                                            p: {
                                                xs: 2.2,
                                                md: 2.8,
                                            },

                                            borderRadius:
                                                "10px",

                                            borderColor:
                                                isPublicAgreed
                                                    ? "#ABEFC6"
                                                    : "#FECDCA",

                                            background:
                                                isPublicAgreed
                                                    ? COLORS.greenLight
                                                    : COLORS.redLight,
                                        }}
                                    >
                                        <MDBox
                                            display="flex"
                                            alignItems="center"
                                            gap={1.5}
                                        >
                                            <MDBox
                                                width={48}
                                                height={48}
                                                borderRadius="8px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    background:
                                                        "#FFFFFF",

                                                    color:
                                                        isPublicAgreed
                                                            ? COLORS.green
                                                            : COLORS.red,

                                                    boxShadow:
                                                        "0 4px 12px rgba(16,24,40,.06)",
                                                }}
                                            >
                                                {isPublicAgreed ? (
                                                    <CheckCircle />
                                                ) : (
                                                    <WarningAmber />
                                                )}
                                            </MDBox>

                                            <MDBox>
                                                <MDTypography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    sx={{
                                                        color:
                                                            isPublicAgreed
                                                                ? COLORS.green
                                                                : COLORS.red,

                                                        letterSpacing:
                                                            ".05em",
                                                    }}
                                                >
                                                    {
                                                        counterpartyResponseTexts
                                                            .title
                                                    }
                                                </MDTypography>

                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{
                                                        color:
                                                            isPublicAgreed
                                                                ? COLORS.green
                                                                : COLORS.red,
                                                    }}
                                                >
                                                    {isPublicAgreed
                                                        ? counterpartyResponseTexts
                                                            .agreed
                                                        : counterpartyResponseTexts
                                                            .disputed}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    </Paper>

                                    <Grid
                                        container
                                        spacing={2.2}
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                        >
                                            <MDTypography
                                                variant="caption"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,
                                                }}
                                            >
                                                {
                                                    counterpartyResponseTexts
                                                        .respondedBy
                                                }
                                            </MDTypography>

                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                                display="block"
                                                mt={0.4}
                                            >
                                                {reconciliation
                                                        ?.counterpartyRespondedBy ||
                                                    "-"}
                                            </MDTypography>
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                        >
                                            <MDTypography
                                                variant="caption"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,
                                                }}
                                            >
                                                {
                                                    counterpartyResponseTexts
                                                        .respondedAt
                                                }
                                            </MDTypography>

                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                                display="block"
                                                mt={0.4}
                                            >
                                                {formatDateTime(
                                                    reconciliation
                                                        ?.counterpartyRespondedAt,
                                                    financeLanguage
                                                )}
                                            </MDTypography>
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                        >
                                            <MDTypography
                                                variant="caption"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,
                                                }}
                                            >
                                                {
                                                    counterpartyResponseTexts
                                                        .systemBalance
                                                }
                                            </MDTypography>

                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                                display="block"
                                                mt={0.4}
                                                sx={{
                                                    color:
                                                    COLORS.navy,
                                                }}
                                            >
                                                {formatMoney(
                                                    currentBalance,
                                                    currency,
                                                    financeLanguage
                                                )}
                                            </MDTypography>
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                        >
                                            <MDTypography
                                                variant="caption"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,
                                                }}
                                            >
                                                {
                                                    counterpartyResponseTexts
                                                        .declaredBalance
                                                }
                                            </MDTypography>

                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                                display="block"
                                                mt={0.4}
                                                sx={{
                                                    color:
                                                        isBalanced
                                                            ? COLORS.green
                                                            : COLORS.red,
                                                }}
                                            >
                                                {formatMoney(
                                                    counterpartyBalance,
                                                    currency,
                                                    financeLanguage
                                                )}
                                            </MDTypography>
                                        </Grid>

                                        {isPublicDisputed ? (
                                            <Grid
                                                item
                                                xs={12}
                                                md={6}
                                            >
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{
                                                        color:
                                                        COLORS.subtext,
                                                    }}
                                                >
                                                    {
                                                        counterpartyResponseTexts
                                                            .balanceType
                                                    }
                                                </MDTypography>

                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    display="block"
                                                    mt={0.4}
                                                >
                                                    {
                                                        responseBalanceTypeLabel
                                                    }
                                                </MDTypography>
                                            </Grid>
                                        ) : null}

                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                        >
                                            <MDTypography
                                                variant="caption"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,
                                                }}
                                            >
                                                {
                                                    counterpartyResponseTexts
                                                        .difference
                                                }
                                            </MDTypography>

                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                                display="block"
                                                mt={0.4}
                                                sx={{
                                                    color:
                                                        isBalanced
                                                            ? COLORS.green
                                                            : COLORS.red,
                                                }}
                                            >
                                                {formatMoney(
                                                    differenceAmount,
                                                    currency,
                                                    financeLanguage
                                                )}
                                            </MDTypography>
                                        </Grid>
                                    </Grid>

                                    {isPublicDisputed &&
                                    responseNote ? (
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                mt: 2.5,

                                                p: 2.2,

                                                borderRadius:
                                                    "8px",

                                                borderColor:
                                                    "#FECDCA",

                                                background:
                                                    "#FFFBFA",
                                            }}
                                        >
                                            <MDTypography
                                                variant="caption"
                                                fontWeight="bold"
                                                sx={{
                                                    color:
                                                    COLORS.red,
                                                }}
                                            >
                                                {
                                                    counterpartyResponseTexts
                                                        .explanation
                                                }
                                            </MDTypography>

                                            <MDTypography
                                                variant="body2"
                                                mt={0.8}
                                                sx={{
                                                    color:
                                                    COLORS.text,

                                                    whiteSpace:
                                                        "pre-wrap",

                                                    wordBreak:
                                                        "break-word",
                                                }}
                                            >
                                                {responseNote}
                                            </MDTypography>
                                        </Paper>
                                    ) : null}

                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            mt: 2.5,

                                            p: 2,

                                            borderRadius:
                                                "8px",

                                            borderColor:
                                            COLORS.border,

                                            background:
                                                "#F8FAFC",
                                        }}
                                    >
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems={{
                                                xs:
                                                    "flex-start",

                                                sm:
                                                    "center",
                                            }}
                                            flexDirection={{
                                                xs:
                                                    "column",

                                                sm:
                                                    "row",
                                            }}
                                            gap={1.5}
                                        >
                                            <MDBox
                                                display="flex"
                                                alignItems="center"
                                                gap={1.2}
                                            >
                                                <AttachFile
                                                    sx={{
                                                        color:
                                                        COLORS.blue,
                                                    }}
                                                />

                                                <MDBox>
                                                    <MDTypography
                                                        variant="button"
                                                        fontWeight="bold"
                                                    >
                                                        {
                                                            counterpartyResponseTexts
                                                                .document
                                                        }
                                                    </MDTypography>

                                                    <MDTypography
                                                        variant="caption"
                                                        display="block"
                                                        sx={{
                                                            color:
                                                            COLORS.subtext,
                                                        }}
                                                    >
                                                        {responseDocumentName ||
                                                            counterpartyResponseTexts
                                                                .noDocument}
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>

                                            {responseDocumentUrl ? (
                                                <MDButton
                                                    variant="contained"
                                                    color="info"
                                                    onClick={() =>
                                                        window.open(
                                                            responseDocumentUrl,
                                                            "_blank",
                                                            "noopener,noreferrer"
                                                        )
                                                    }
                                                    sx={{
                                                        minHeight:
                                                            "40px",

                                                        color:
                                                            "#FFFFFF !important",
                                                    }}
                                                >
                                                    <AttachFile
                                                        sx={{
                                                            mr: 0.5,
                                                        }}
                                                    />

                                                    {
                                                        counterpartyResponseTexts
                                                            .viewDocument
                                                    }
                                                </MDButton>
                                            ) : null}
                                        </MDBox>
                                    </Paper>
                                </MDBox>
                            ) : (
                                <MDBox
                                    p={3}
                                >
                                    <Alert
                                        severity="info"
                                        sx={{
                                            borderRadius:
                                                "8px",
                                        }}
                                    >
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                        >
                                            {
                                                counterpartyResponseTexts
                                                    .waiting
                                            }
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            mt={0.4}
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {
                                                counterpartyResponseTexts
                                                    .waitingDescription
                                            }
                                        </MDTypography>
                                    </Alert>
                                </MDBox>
                            )}
                        </Card>

                        {/* PARTY INFORMATION */}

                        <Card
                            sx={cardSx}
                        >
                            <MDBox
                                px={3}
                                py={2.7}
                            >
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                >
                                    {financeText(
                                        "Counterparty Information"
                                    )}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    mt={0.4}
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {financeText(
                                        "Reconciliation counterparty details"
                                    )}
                                </MDTypography>
                            </MDBox>

                            <Divider />

                            <MDBox
                                p={3}
                            >
                                <Grid
                                    container
                                    spacing={2.5}
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText(
                                                "Company"
                                            )}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            mt={0.4}
                                        >
                                            {reconciliation
                                                    ?.snapshot
                                                    ?.party
                                                    ?.name ||
                                                "-"}
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText(
                                                "Email"
                                            )}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            mt={0.4}
                                        >
                                            {reconciliation
                                                    ?.snapshot
                                                    ?.party
                                                    ?.email ||
                                                "-"}
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText(
                                                "Tax Number"
                                            )}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            mt={0.4}
                                        >
                                            {reconciliation
                                                    ?.snapshot
                                                    ?.party
                                                    ?.taxNumber ||
                                                "-"}
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {
                                                counterpartyResponseTexts
                                                    .taxOffice
                                            }
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            mt={0.4}
                                        >
                                            {reconciliation
                                                    ?.snapshot
                                                    ?.party
                                                    ?.taxOffice ||
                                                "-"}
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {
                                                counterpartyResponseTexts
                                                    .phone
                                            }
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            mt={0.4}
                                        >
                                            {reconciliation
                                                    ?.snapshot
                                                    ?.party
                                                    ?.phone ||
                                                "-"}
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText(
                                                "Balance Date"
                                            )}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            mt={0.4}
                                        >
                                            {formatDate(
                                                reconciliation
                                                    .periodEnd,
                                                financeLanguage
                                            )}
                                        </MDTypography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {financeText(
                                                "Address"
                                            )}
                                        </MDTypography>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            mt={0.4}
                                        >
                                            {reconciliation
                                                    ?.snapshot
                                                    ?.party
                                                    ?.address ||
                                                "-"}
                                        </MDTypography>
                                    </Grid>
                                </Grid>
                            </MDBox>
                        </Card>
                    </Grid>

                    {/* ================================================= */}
                    {/* RIGHT COLUMN */}
                    {/* ================================================= */}

                    <Grid
                        item
                        xs={12}
                        xl={4}
                    >
                        {/* BALANCE CONFIRMATION */}

                        <Card
                            sx={{
                                ...cardSx,

                                mb: 2.5,
                            }}
                        >
                            <MDBox
                                p={2.5}
                            >
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                >
                                    {financeText(
                                        "Balance Confirmation"
                                    )}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    mt={0.5}
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {financeText(
                                        "Enter the balance declared by the counterparty."
                                    )}
                                </MDTypography>

                                <TextField
                                    fullWidth
                                    type="number"
                                    label={
                                        financeText(
                                            "Counterparty Balance"
                                        )
                                    }
                                    value={
                                        form
                                            .counterpartyBalance
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleFormChange(
                                            "counterpartyBalance",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    disabled={
                                        hasPublicResponse ||
                                        currentStatus ===
                                        "confirmed" ||
                                        currentStatus ===
                                        "cancelled"
                                    }
                                    helperText={
                                        hasPublicResponse
                                            ? counterpartyResponseTexts.publicResponse
                                            : ""
                                    }
                                    sx={{
                                        ...inputSx,

                                        mt: 2,
                                    }}
                                />

                                <Paper
                                    variant="outlined"
                                    sx={{
                                        mt: 2,

                                        p: 2,

                                        borderRadius:
                                            "8px",

                                        borderColor:
                                        COLORS.border,

                                        background:
                                            "#F8FAFC",
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText(
                                            "Current System Balance"
                                        )}
                                    </MDTypography>

                                    <MDTypography
                                        variant="h6"
                                        fontWeight="bold"
                                        mt={0.5}
                                        sx={{
                                            color:
                                            COLORS.navy,
                                        }}
                                    >
                                        {formatMoney(
                                            currentBalance,
                                            currency,
                                            financeLanguage
                                        )}
                                    </MDTypography>
                                </Paper>

                                {counterpartyBalance !==
                                null ? (
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            mt: 1.5,

                                            p: 2,

                                            borderRadius:
                                                "8px",

                                            borderColor:
                                                isBalanced
                                                    ? "#ABEFC6"
                                                    : "#FECDCA",

                                            background:
                                                isBalanced
                                                    ? COLORS.greenLight
                                                    : COLORS.redLight,
                                        }}
                                    >
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            gap={2}
                                        >
                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                            >
                                                {financeText(
                                                    "Difference"
                                                )}
                                            </MDTypography>

                                            <MDTypography
                                                variant="button"
                                                fontWeight="bold"
                                                sx={{
                                                    color:
                                                        isBalanced
                                                            ? COLORS.green
                                                            : COLORS.red,
                                                }}
                                            >
                                                {formatMoney(
                                                    differenceAmount,
                                                    currency,
                                                    financeLanguage
                                                )}
                                            </MDTypography>
                                        </MDBox>
                                    </Paper>
                                ) : null}
                            </MDBox>
                        </Card>

                        {/* NOTES */}

                        <Card
                            sx={{
                                ...cardSx,

                                mb: 2.5,
                            }}
                        >
                            <MDBox
                                p={2.5}
                            >
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Notes
                                        sx={{
                                            color:
                                            COLORS.blue,
                                        }}
                                    />

                                    <MDTypography
                                        variant="h6"
                                        fontWeight="bold"
                                    >
                                        {financeText(
                                            "Notes"
                                        )}
                                    </MDTypography>
                                </MDBox>

                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label={
                                        financeText(
                                            "Internal Notes"
                                        )
                                    }
                                    value={
                                        form.notes
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleFormChange(
                                            "notes",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    sx={{
                                        ...multilineInputSx,

                                        mt: 2,
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label={
                                        financeText(
                                            "Dispute Note"
                                        )
                                    }
                                    value={
                                        form
                                            .disputeNote
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleFormChange(
                                            "disputeNote",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    sx={{
                                        ...multilineInputSx,

                                        mt: 2,
                                    }}
                                />

                                <MDButton
                                    fullWidth
                                    color="info"
                                    variant="contained"
                                    onClick={
                                        handleSave
                                    }
                                    disabled={
                                        saving
                                    }
                                    sx={{
                                        mt: 2,

                                        minHeight:
                                            "44px",

                                        color:
                                            "#FFFFFF !important",
                                    }}
                                >
                                    {saving ? (
                                        <CircularProgress
                                            size={17}
                                            color="inherit"
                                        />
                                    ) : (
                                        <>
                                            <Save
                                                sx={{
                                                    mr: 0.5,
                                                }}
                                            />

                                            {financeText(
                                                "Save Changes"
                                            )}
                                        </>
                                    )}
                                </MDButton>
                            </MDBox>
                        </Card>

                        {/* WORKFLOW */}

                        <Card
                            sx={cardSx}
                        >
                            <MDBox
                                p={2.5}
                            >
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                >
                                    {financeText(
                                        "Reconciliation Workflow"
                                    )}
                                </MDTypography>

                                <MDBox
                                    mt={2}
                                    p={1.8}
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    sx={{
                                        background:
                                        statusConfig.background,

                                        color:
                                        statusConfig.textColor,
                                    }}
                                >
                                    {statusConfig.icon}

                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                "inherit",
                                        }}
                                    >
                                        {financeText(
                                            statusConfig.label
                                        )}
                                    </MDTypography>
                                </MDBox>

                                {availableStatuses.length >
                                0 ? (
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            ...inputSx,

                                            mt: 2,
                                        }}
                                    >
                                        <InputLabel>
                                            {financeText(
                                                "Change Status"
                                            )}
                                        </InputLabel>

                                        <Select
                                            value=""
                                            label={
                                                financeText(
                                                    "Change Status"
                                                )
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                openStatusDialog(
                                                    event
                                                        .target
                                                        .value
                                                )
                                            }
                                        >
                                            {availableStatuses.map(
                                                (
                                                    status
                                                ) => (
                                                    <MenuItem
                                                        key={
                                                            status
                                                        }
                                                        value={
                                                            status
                                                        }
                                                    >
                                                        {financeText(
                                                            getStatusConfig(
                                                                status
                                                            )
                                                                .label
                                                        )}
                                                    </MenuItem>
                                                )
                                            )}
                                        </Select>
                                    </FormControl>
                                ) : null}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* ===================================================== */}
            {/* SEND EMAIL DIALOG */}
            {/* ===================================================== */}

            <Dialog
                open={
                    sendDialogOpen
                }
                onClose={
                    closeSendDialog
                }
                fullWidth
                maxWidth="md"
                disableEscapeKeyDown={
                    sendLoading
                }
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",

                        overflow:
                            "hidden",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        p: 0,
                    }}
                >
                    <MDBox
                        p={3}
                        sx={{
                            background:
                                "#0B1F3A",
                        }}
                    >
                        <MDBox
                            display="flex"
                            alignItems="center"
                            gap={1.2}
                        >
                            <Email
                                sx={{
                                    color:
                                        "#FFFFFF",
                                }}
                            />

                            <MDTypography
                                variant="h5"
                                fontWeight="bold"
                                sx={{
                                    color:
                                        "#FFFFFF",
                                }}
                            >
                                {financeText(
                                    "Send Reconciliation"
                                )}
                            </MDTypography>
                        </MDBox>

                        <MDTypography
                            variant="caption"
                            sx={{
                                color:
                                    "rgba(255,255,255,.72)",
                            }}
                        >
                            {financeText(
                                "Send the reconciliation PDF to the counterparty."
                            )}
                        </MDTypography>
                    </MDBox>
                </DialogTitle>

                <DialogContent>
                    <MDBox
                        pt={3}
                    >
                        {sendError ? (
                            <Alert
                                severity="error"
                                sx={{
                                    mb: 2.5,

                                    borderRadius:
                                        "8px",
                                }}
                            >
                                {sendError}
                            </Alert>
                        ) : null}

                        <Grid
                            container
                            spacing={2}
                        >
                            <Grid
                                item
                                xs={12}
                                md={8}
                            >
                                <TextField
                                    fullWidth
                                    label={
                                        financeText(
                                            "Recipient Email"
                                        )
                                    }
                                    value={
                                        emailForm.to
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleEmailFormChange(
                                            "to",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    sx={
                                        inputSx
                                    }
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={4}
                            >
                                <FormControl
                                    fullWidth
                                    sx={
                                        inputSx
                                    }
                                >
                                    <InputLabel>
                                        {financeText(
                                            "Language"
                                        )}
                                    </InputLabel>

                                    <Select
                                        value={
                                            emailForm.language
                                        }
                                        label={
                                            financeText(
                                                "Language"
                                            )
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleEmailFormChange(
                                                "language",
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    >
                                        <MenuItem value="en">
                                            English
                                        </MenuItem>

                                        <MenuItem value="tr">
                                            Türkçe
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                            >
                                <TextField
                                    fullWidth
                                    label={
                                        financeText(
                                            "CC"
                                        )
                                    }
                                    value={
                                        emailForm.cc
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleEmailFormChange(
                                            "cc",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    sx={
                                        inputSx
                                    }
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                            >
                                <TextField
                                    fullWidth
                                    label={
                                        financeText(
                                            "Subject"
                                        )
                                    }
                                    value={
                                        emailForm.subject
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleEmailFormChange(
                                            "subject",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    sx={
                                        inputSx
                                    }
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                            >
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={7}
                                    label={
                                        financeText(
                                            "Message"
                                        )
                                    }
                                    value={
                                        emailForm.message
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleEmailFormChange(
                                            "message",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    sx={
                                        multilineInputSx
                                    }
                                />
                            </Grid>
                        </Grid>

                        <Paper
                            variant="outlined"
                            sx={{
                                mt: 2.5,

                                p: 2,

                                borderRadius:
                                    "8px",

                                background:
                                COLORS.blueLight,

                                borderColor:
                                    "#B2DDFF",
                            }}
                        >
                            <MDBox
                                display="flex"
                                alignItems="center"
                                gap={1.2}
                            >
                                <AttachFile
                                    sx={{
                                        color:
                                        COLORS.blue,
                                    }}
                                />

                                <MDBox>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                    >
                                        {financeText(
                                            "PDF Attachment"
                                        )}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {reconciliation
                                                ?.reconciliationNo ||
                                            "Reconciliation"}

                                        {" - "}

                                        {emailForm
                                            .language
                                            .toUpperCase()}

                                        .pdf
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        </Paper>

                        <Alert
                            severity="info"
                            sx={{
                                mt: 2,

                                borderRadius:
                                    "8px",
                            }}
                        >
                            {financeText(
                                "The PDF will be generated automatically using the selected language."
                            )}
                        </Alert>
                    </MDBox>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 3,
                        pt: 1,
                    }}
                >
                    <Button
                        onClick={
                            closeSendDialog
                        }
                        disabled={
                            sendLoading
                        }
                    >
                        {financeText(
                            "Cancel"
                        )}
                    </Button>

                    <MDButton
                        variant="contained"
                        color="info"
                        onClick={
                            handleSendEmail
                        }
                        disabled={
                            sendLoading
                        }
                        sx={{
                            minWidth:
                                160,

                            minHeight:
                                "44px",

                            color:
                                "#FFFFFF !important",
                        }}
                    >
                        {sendLoading ? (
                            <>
                                <CircularProgress
                                    size={17}
                                    color="inherit"
                                    sx={{
                                        mr: 1,
                                    }}
                                />

                                {financeText(
                                    "Sending..."
                                )}
                            </>
                        ) : (
                            <>
                                <Send
                                    sx={{
                                        mr: 0.7,
                                    }}
                                />

                                {financeText(
                                    "Send Email"
                                )}
                            </>
                        )}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* ===================================================== */}
            {/* STATUS DIALOG */}
            {/* ===================================================== */}

            <Dialog
                open={
                    statusDialogOpen
                }
                onClose={
                    closeStatusDialog
                }
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",
                    },
                }}
            >
                <DialogTitle>
                    {financeText(
                        "Update Reconciliation Status"
                    )}
                </DialogTitle>

                <DialogContent>
                    <MDBox
                        pt={2}
                    >
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label={
                                selectedStatus ===
                                "disputed"
                                    ? financeText(
                                        "Dispute Note"
                                    )
                                    : financeText(
                                        "Status Note"
                                    )
                            }
                            value={
                                statusNote
                            }
                            onChange={(
                                event
                            ) =>
                                setStatusNote(
                                    event
                                        .target
                                        .value
                                )
                            }
                            sx={
                                multilineInputSx
                            }
                        />

                        {statusError ? (
                            <Alert
                                severity="error"
                                sx={{
                                    mt: 2,

                                    borderRadius:
                                        "8px",
                                }}
                            >
                                {statusError}
                            </Alert>
                        ) : null}
                    </MDBox>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 3,
                    }}
                >
                    <Button
                        onClick={
                            closeStatusDialog
                        }
                    >
                        {financeText(
                            "Cancel"
                        )}
                    </Button>

                    <MDButton
                        variant="contained"
                        color="info"
                        onClick={
                            handleStatusUpdate
                        }
                        disabled={
                            statusLoading
                        }
                        sx={{
                            minHeight:
                                "44px",

                            color:
                                "#FFFFFF !important",
                        }}
                    >
                        {statusLoading ? (
                            <CircularProgress
                                size={17}
                                color="inherit"
                            />
                        ) : (
                            financeText(
                                "Update Status"
                            )
                        )}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
}

export default ReconciliationDetail;
